package ai.jessie.web.documents

import ai.jessie.shared.documents.DocumentDetailResponse
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.CardBodyStyle
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.FieldInputStyle
import ai.jessie.web.theme.FieldStyle
import ai.jessie.web.theme.FieldsetStyle
import ai.jessie.web.theme.LegendStyle
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.SmallStyle
import ai.jessie.web.theme.SubListStyle
import ai.jessie.web.theme.TextAreaStyle
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.forId
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Fieldset
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Legend
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea
import org.jetbrains.compose.web.dom.Ul
import org.w3c.dom.HTMLElement

// Steps 9-10, A6-A12, BR-136: the complete outcome, its required input, then confirm or cancel.
@Composable
internal fun documentConfirmation(
    detail: DocumentDetailResponse,
    confirmation: DocumentConfirmation,
    state: DocumentWorkflowUiState,
    actions: DocumentWorkflowActions,
) {
    Form(
        attrs =
            CardStyle.toAttrs {
                id(CONFIRMATION_ID)
                attr("role", "alertdialog")
                attr("aria-labelledby", CONFIRMATION_TITLE_ID)
                attr("aria-describedby", CONFIRMATION_TEXT_ID)
                attr("tabindex", "-1")
                addEventListener("submit") {
                    it.preventDefault()
                    actions.confirm()
                }
            }
    ) {
        Fieldset(attrs = FieldsetStyle.toAttrs { if (state.busy) attr("disabled", "") }) {
            Legend(attrs = LegendStyle.toAttrs { id(CONFIRMATION_TITLE_ID) }) {
                Text(confirmationTitle(detail, confirmation))
            }
            Div(attrs = CardBodyStyle.toAttrs()) {
                P(attrs = SmallStyle.toAttrs { id(CONFIRMATION_TEXT_ID) }) {
                    Text(confirmationText(detail, confirmation))
                }
                confirmationInput(detail, confirmation, state, actions)
                Div(attrs = RowStyle.toAttrs()) {
                    themedButton(
                        confirmationLabel(detail, confirmation),
                        {},
                        variant = confirmationVariant(confirmation),
                        submit = true,
                    )
                    themedButton("Abbrechen", actions.cancel)
                }
            }
        }
    }
    LaunchedEffect(confirmation.step, confirmation.target) {
        if (!AppGlobals.isExporting)
            (document.getElementById(CONFIRMATION_ID) as? HTMLElement)?.focus()
    }
}

@Composable
private fun confirmationInput(
    detail: DocumentDetailResponse,
    confirmation: DocumentConfirmation,
    state: DocumentWorkflowUiState,
    actions: DocumentWorkflowActions,
) {
    when (confirmation.step) {
        DocumentStep.TRANSITION -> {
            val target = confirmation.target.orEmpty()
            if (reasonRequired(detail.status, target)) reasonInput(confirmation, state, actions)
            else previewChanges(detail, confirmation)
        }
        DocumentStep.PAYMENT -> paymentInput(detail, confirmation, state, actions)
        DocumentStep.CLEAR_PAYMENT -> Unit
        DocumentStep.REMOVAL -> {
            confirmation.removal?.let {
                facts(it.fileName, detail.mediaType, it.sizeBytes, detail.uploadedAt)
            }
            reasonInput(confirmation, state, actions)
        }
    }
}

// Step 9: the validated outcome, nothing applied yet (A13).
@Composable
private fun previewChanges(detail: DocumentDetailResponse, confirmation: DocumentConfirmation) {
    P(attrs = SmallStyle.toAttrs()) {
        Text(
            "Status: ${documentStatusLabel(detail.status)} → " +
                documentStatusLabel(confirmation.target.orEmpty())
        )
    }
    val changes = confirmation.changes.filter { it.field != "status" }
    if (changes.isEmpty()) {
        P(attrs = SmallStyle.toAttrs()) { Text("Weitere Angaben ändern sich nicht.") }
        return
    }
    P(attrs = SmallStyle.toAttrs()) { Text("Zusammen mit dem Statuswechsel gespeichert:") }
    Ul(attrs = listOf(SubListStyle, SmallStyle).toAttrs()) {
        changes.forEach { change ->
            Li {
                Text(
                    "${documentFieldLabel(change.field)}: " +
                        "${documentChangeValue(change.field, change.previous, detail)} → " +
                        documentChangeValue(change.field, change.new, detail)
                )
            }
        }
    }
}

// A9-A11: required, at most 1000 characters; the server validates again.
@Composable
private fun reasonInput(
    confirmation: DocumentConfirmation,
    state: DocumentWorkflowUiState,
    actions: DocumentWorkflowActions,
) {
    val problem = state.problems["reason"]
    Div(attrs = FieldStyle.toAttrs()) {
        Label(attrs = { forId(CONFIRMATION_INPUT_ID) }) {
            Text("Grund (Pflichtfeld, höchstens $MAX_REASON Zeichen)")
        }
        TextArea(
            value = confirmation.input,
            attrs =
                TextAreaStyle.toAttrs {
                    id(CONFIRMATION_INPUT_ID)
                    attr("name", "reason")
                    attr("rows", "3")
                    attr("required", "")
                    attr("maxlength", "$MAX_REASON")
                    problemAttributes(problem, CONFIRMATION_INPUT_ID)
                    onInput { actions.input(it.value) }
                },
        )
        fieldProblem(problem, CONFIRMATION_INPUT_ID)
    }
}

// A6: the permitted range from the saved document date through organisation-local today.
@Composable
private fun paymentInput(
    detail: DocumentDetailResponse,
    confirmation: DocumentConfirmation,
    state: DocumentWorkflowUiState,
    actions: DocumentWorkflowActions,
) {
    val problem = state.problems[PAID_ON] ?: state.problems["documentDate"]
    val from = detail.details.documentDate
    Div(attrs = FieldStyle.toAttrs()) {
        Label(attrs = { forId(CONFIRMATION_INPUT_ID) }) {
            Text(
                "Zahlungsdatum (Pflichtfeld, ${from?.let { "$it bis " }.orEmpty()}${detail.today})"
            )
        }
        Input(
            InputType.Date,
            attrs =
                FieldInputStyle.toAttrs {
                    id(CONFIRMATION_INPUT_ID)
                    attr("name", PAID_ON)
                    attr("required", "")
                    from?.let { attr("min", it) }
                    attr("max", detail.today)
                    value(confirmation.input)
                    problemAttributes(problem, CONFIRMATION_INPUT_ID)
                    onInput { actions.input(it.value) }
                },
        )
        fieldProblem(problem, CONFIRMATION_INPUT_ID)
    }
}

private fun confirmationTitle(detail: DocumentDetailResponse, confirmation: DocumentConfirmation) =
    when (confirmation.step) {
        DocumentStep.TRANSITION -> transitionLabel(detail.status, confirmation.target.orEmpty())
        DocumentStep.PAYMENT ->
            if (detail.paidOn == null) "Zahlung erfassen" else "Zahlung korrigieren"
        DocumentStep.CLEAR_PAYMENT -> "Zahlung entfernen"
        DocumentStep.REMOVAL -> "Dokument endgültig löschen"
    }

private fun confirmationText(detail: DocumentDetailResponse, confirmation: DocumentConfirmation) =
    when (confirmation.step) {
        DocumentStep.TRANSITION ->
            transitionConsequences(detail.status, confirmation.target.orEmpty())
        DocumentStep.PAYMENT -> paymentConsequences(detail)
        DocumentStep.CLEAR_PAYMENT ->
            "Das erfasste Zahlungsdatum ${detail.paidOn.orEmpty()} wird entfernt. Die Änderung " +
                "wird im Verlauf festgehalten."
        DocumentStep.REMOVAL ->
            "Das Dokument wird mit seiner Datei endgültig gelöscht. Das kann nicht rückgängig " +
                "gemacht werden. Der Verlauf der Organisation hält die Löschung mit dir, dem Grund " +
                "und der Zeit fest, ohne den Inhalt."
    }

private fun paymentConsequences(detail: DocumentDetailResponse): String {
    val base =
        if (detail.details.documentDate == null)
            "Für eine Zahlung braucht das Dokument ein gespeichertes Dokumentdatum."
        else "Das Zahlungsdatum bestimmt die Periode der Ist-Versteuerung."
    val linked =
        if (detail.linkedInvoice)
            " Die verknüpfte Rechnung wird mit demselben Datum als bezahlt markiert; danach kann " +
                "die Zahlung nicht mehr geändert werden."
        else ""
    return base + linked
}

private fun confirmationLabel(detail: DocumentDetailResponse, confirmation: DocumentConfirmation) =
    when (confirmation.step) {
        DocumentStep.TRANSITION ->
            transitionConfirmLabel(detail.status, confirmation.target.orEmpty())
        DocumentStep.PAYMENT -> "Zahlung bestätigen"
        DocumentStep.CLEAR_PAYMENT -> "Entfernen bestätigen"
        DocumentStep.REMOVAL -> "Endgültig löschen"
    }

private fun confirmationVariant(confirmation: DocumentConfirmation) =
    when {
        confirmation.step == DocumentStep.REMOVAL ||
            confirmation.step == DocumentStep.CLEAR_PAYMENT ||
            confirmation.target == REJECTED -> ButtonVariant.DANGER
        else -> ButtonVariant.PRIMARY
    }

// Distinct from the UC-017 upload confirmation shown on the same Inbox page.
private const val CONFIRMATION_ID = "document-workflow-confirmation"
private const val CONFIRMATION_TITLE_ID = "document-workflow-confirmation-title"
private const val CONFIRMATION_TEXT_ID = "document-workflow-consequences"
